import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Clock, MapPin, Users, Check, ChevronDown, ChevronUp, Loader2, XCircle, Calendar, CreditCard, Phone, Mail, User } from 'lucide-react';
import { client } from '../api/client';
import { localizedName, localizedDescription } from '../lib/localized';
import { normalizeHKPhone } from '../lib/phone';
import { getRefCode } from '../lib/referral';

interface PricingOption {
  type: 'shared' | 'single';
  label: string;
  price: number;
  currency: string;
}

interface Package {
  id: number;
  name: string;
  nameEn: string | null;
  nameZh: string | null;
  nameZhCn: string | null;
  slug: string;
  description: string | null;
  descriptionEn: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  duration: string | null;
  location: string | null;
  audience: string | null;
  inclusions: string[];
  itinerary: { day: string; title: string; desc: string }[];
  pricingOptions: PricingOption[];
  terms: string | null;
  imageUrl: string | null;
  gallery: string[];
  sortOrder: number;
  status: 'active' | 'inactive';
}

const statusMap: Record<string, string> = {
  pending: 'status.pending',
  confirmed: 'status.confirmed',
  cancelled: 'status.cancelled',
  completed: 'status.completed',
};

const paymentStatusMap: Record<string, string> = {
  unpaid: 'paymentStatus.unpaid',
  partial: 'paymentStatus.partial',
  paid: 'paymentStatus.paid',
  refunded: 'paymentStatus.refunded',
};

const USD_TO_HKD = 7.8;
function displayHkd(price: number, currency: string): string {
  if (currency === 'USD') return `HK$${Math.round(price * USD_TO_HKD).toLocaleString()}`;
  return `HK$${price.toLocaleString()}`;
}

export default function PackageDetailPage() {
  const { t } = useTranslation('packages');
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Booking form state
  const [checkIn, setCheckIn] = useState('');
  const [occupancy, setOccupancy] = useState<'shared' | 'single'>('shared');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('+852 ');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [bookingResult, setBookingResult] = useState<{ id: number; token: string } | null>(null);
  const [expandedItinerary, setExpandedItinerary] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!slug) {
        setError(t('error.invalidLink'));
        setLoading(false);
        return;
      }
      try {
        const res = await client.api.fetch(`/api/public/packages/${encodeURIComponent(slug)}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || t('error.loadFailed'));
        }
        const json = await res.json();
        if (mounted) setPkg(json.data || null);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : t('error.loadFailed'));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [slug, t]);

  const selectedOption = useMemo(() => {
    return pkg?.pricingOptions?.find((o) => o.type === occupancy) || pkg?.pricingOptions?.[0];
  }, [pkg, occupancy]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pkg) return;
    setSubmitError('');
    setSubmitting(true);

    try {
      const normalizedPhone = normalizeHKPhone(phone);
      if (normalizedPhone.replace(/\D/g, '').length < 8) {
        throw new Error(t('error.invalidPhone'));
      }
      if (!checkIn) throw new Error(t('error.selectDate'));
      if (!name.trim() || !email.trim()) throw new Error(t('error.fillNameEmail'));

      const checkInTs = Math.floor(new Date(checkIn).getTime() / 1000);
      const body = {
        package_id: pkg.id,
        check_in: checkInTs,
        occupancy,
        name: name.trim(),
        email: email.trim(),
        phone: normalizedPhone,
        referral_code: getRefCode(),
      };

      const res = await client.api.fetch('/api/public/package-bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('error.submitFailed'));
      setBookingResult(data.data || null);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('error.submitGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="pt-32 pb-20 text-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
        <p>{t('loading')}</p>
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="pt-32 pb-20 px-4 text-center">
        <div className="max-w-md mx-auto bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#0d1b2a] mb-2">{t('error.title')}</h1>
          <p className="text-gray-600">{error || t('error.notFound')}</p>
        </div>
      </div>
    );
  }

  const allImages = [pkg.imageUrl, ...pkg.gallery].filter(Boolean) as string[];
  const fallbackImage = 'https://images.unsplash.com/photo-1540206351-d6465b3ac5c1?w=1920&q=80';

  return (
    <div className="pt-20 pb-16 bg-[#f8fafb]">
      {/* Hero */}
      <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img
          src={pkg.imageUrl || fallbackImage}
          alt={localizedName(pkg)}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a4c6b]/90 via-[#0a4c6b]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-6xl mx-auto text-white">
            <p className="text-[#B8902F] font-medium mb-2 tracking-widest uppercase text-sm">{t('hero.eyebrow')}</p>
            <h1 className="text-3xl md:text-5xl font-bold mb-3">{localizedName(pkg)}</h1>
            
            <div className="flex flex-wrap gap-4 text-sm text-white/80">
              {pkg.duration && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {pkg.duration}
                </span>
              )}
              {pkg.location && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> {pkg.location}
                </span>
              )}
              {pkg.audience && (
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" /> {pkg.audience}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100"
            >
              <h2 className="text-xl font-bold text-[#0d1b2a] mb-4">{t('sections.description')}</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                {localizedDescription(pkg) || t('noDescription')}
              </p>
            </motion.div>

            {/* Gallery */}
            {allImages.length > 1 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-[#0d1b2a] mb-4">{t('sections.gallery')}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {allImages.slice(1).map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`${localizedName(pkg)} ${idx + 2}`}
                      className="w-full h-32 md:h-40 object-cover rounded-xl"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Inclusions */}
            {pkg.inclusions && pkg.inclusions.length > 0 && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-[#0d1b2a] mb-4">{t('sections.inclusions')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {pkg.inclusions.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-gray-600">
                      <Check className="w-4 h-4 text-[#2ec4b6] mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Itinerary */}
            {pkg.itinerary && pkg.itinerary.length > 0 && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <button
                  onClick={() => setExpandedItinerary(!expandedItinerary)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h2 className="text-xl font-bold text-[#0d1b2a]">{t('sections.itinerary')}</h2>
                  {expandedItinerary ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                {expandedItinerary && (
                  <div className="mt-4 space-y-4">
                    {pkg.itinerary.map((item, idx) => (
                      <div key={idx} className="flex gap-4 border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                        <span className="font-bold text-[#B8902F] min-w-[4.5rem]">{item.day}</span>
                        <div>
                          <p className="font-medium text-[#0d1b2a]">{item.title}</p>
                          <p className="text-gray-500 text-sm mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Terms */}
            {pkg.terms && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-[#0d1b2a] mb-4">{t('sections.terms')}</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line text-sm">{pkg.terms}</p>
              </div>
            )}
          </div>

          {/* Booking form sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                {bookingResult ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0d1b2a] mb-2">{t('success.title')}</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {t('success.orderId', { id: bookingResult.id })}<br />
                      {t('success.followUp')}
                    </p>
                    <button
                      onClick={() => navigate(`/package-order/${bookingResult.token}`)}
                      className="w-full bg-[#0a4c6b] text-white py-3 rounded-xl font-semibold hover:bg-[#083d56] transition"
                    >
                      {t('success.viewOrder')}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <h3 className="text-lg font-bold text-[#0d1b2a] mb-1">{t('form.title')}</h3>
                    <p className="text-sm text-gray-500 mb-4">{t('form.subtitle')}</p>

                    {/* Check-in */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.checkIn')}</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="date"
                          value={checkIn}
                          onChange={(e) => setCheckIn(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
                        />
                      </div>
                    </div>

                    {/* Occupancy */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('form.roomType')}</label>
                      <div className="space-y-2">
                        {pkg.pricingOptions?.map((opt) => (
                          <label
                            key={opt.type}
                            className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition ${
                              occupancy === opt.type
                                ? 'border-[#0a4c6b] bg-[#0a4c6b]/5'
                                : 'border-gray-200 hover:border-[#2ec4b6]'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="occupancy"
                                value={opt.type}
                                checked={occupancy === opt.type}
                                onChange={(e) => setOccupancy(e.target.value as 'shared' | 'single')}
                              />
                              <span className="text-sm font-medium text-[#0d1b2a]">{opt.label}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-bold text-[#0a4c6b]">
                                {displayHkd(opt.price, opt.currency)}
                              </span>
                              <span className="text-xs text-gray-400 ml-1">{t('form.originalPrice', { currency: opt.currency, price: opt.price.toLocaleString() })}</span>
                            </div>
                          </label>
                        ))}
                        {(!pkg.pricingOptions || pkg.pricingOptions.length === 0) && (
                          <p className="text-sm text-gray-400">{t('form.noPricing')}</p>
                        )}
                      </div>
                    </div>

                    {/* Live total */}
                    {selectedOption && (
                      <div className="flex items-center justify-between bg-[#f0f9f7] rounded-xl px-4 py-3">
                        <span className="text-sm text-gray-600">{t('form.total')}</span>
                        <div className="text-right">
                          <span className="text-lg font-bold text-[#0a4c6b]">
                            {displayHkd(selectedOption.price, selectedOption.currency)}
                          </span>
                          <p className="text-xs text-gray-400 mt-1">
                            {t('form.rateNote')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Contact */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.name')}</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          required
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
                          placeholder={t('form.namePlaceholder')}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.email')}</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          required
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
                          placeholder={t('form.emailPlaceholder')}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.phone')}</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          required
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
                          placeholder={t('form.phonePlaceholder')}
                        />
                      </div>
                    </div>

                    {submitError && <p className="text-sm text-red-600">{submitError}</p>}

                    <button
                      type="submit"
                      disabled={submitting || !selectedOption}
                      className="w-full bg-[#B8902F] text-white py-3 rounded-xl font-semibold hover:bg-[#9a7a28] transition disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      <CreditCard className="w-4 h-4" />
                      {submitting ? t('form.submitting') : t('form.submit')}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      {t('form.disclaimer')}
                    </p>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
